import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Paper, Typography, VersionBadge } from '@shared/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadArtifactsCsv } from './ArtifactsDashboardPage';
import IdentityResolutionCard from './components/IdentityResolutionCard';
import { downloadAdminCsv } from './utils/downloadAdminCsv';

export default function AdminHomePage() {
  const navigate = useNavigate();
  const [generatedArtifactsExportError, setGeneratedArtifactsExportError] = useState<string | null>(
    null
  );
  const [artifactExportError, setArtifactExportError] = useState<string | null>(null);
  const [isArtifactExporting, setIsArtifactExporting] = useState(false);

  async function handleGeneratedArtifactsExport() {
    setGeneratedArtifactsExportError(null);

    try {
      await downloadArtifactsCsv();
    } catch (error) {
      setGeneratedArtifactsExportError(error instanceof Error ? error.message : 'Export failed');
    }
  }

  async function handleArtifactExport() {
    if (isArtifactExporting) {
      return;
    }

    setArtifactExportError(null);
    setIsArtifactExporting(true);

    try {
      await downloadAdminCsv({
        target: 'admin.artifacts.export_csv',
      });
    } catch (error) {
      setArtifactExportError(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsArtifactExporting(false);
    }
  }

  return (
    <Box
      sx={{
        maxWidth: 880,
        margin: '0 auto',
        padding: 3,
      }}
    >
      <Typography variant="h4" gutterBottom>
        Admin Area
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Use the sidebar to access administrative functions.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <VersionBadge />
      </Box>

      <Box sx={{ display: 'grid', gap: 3 }}>
        <IdentityResolutionCard />

        <Paper variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
          <Typography variant="h6" gutterBottom>
            Artifacts generated
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Volume and breakdowns for artifacts produced across the platform — by artifact type,
            coach, and org. Includes date-range filters and a CSV export.
          </Typography>
          {generatedArtifactsExportError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {generatedArtifactsExportError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<OpenInNewIcon />}
              onClick={() => navigate('/admin/artifacts')}
            >
              Open dashboard
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleGeneratedArtifactsExport}
            >
              Download CSV
            </Button>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
          <Typography variant="h6" gutterBottom>
            Member reflection backfill
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review pre-flag member-reflection coverage, prepare readiness gaps, and create bounded
            historical backfill runs without changing feature flags.
          </Typography>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={() => navigate('/admin/member-reflection-backfill')}
          >
            Open backfill dashboard
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
          <Typography variant="h6" gutterBottom>
            Artifact CSV
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download one row per artifact with resolved coach, member, partner, session, generation,
            sharing, revision, and chat-count fields.
          </Typography>
          {artifactExportError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {artifactExportError}
            </Alert>
          )}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleArtifactExport}
            disabled={isArtifactExporting}
          >
            Download artifact CSV
          </Button>
        </Paper>
      </Box>
    </Box>
  );
}
